import React, { useState, useContext, useEffect } from 'react'
import { XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart, Brush, ReferenceLine } from 'recharts';
import { format } from 'date-fns';
import Loading from '../../Loading';
import TimeRangeContainer from '../../utils/TimeRangeContainer';
import { UserSettingsContext } from '../../../context/UserSettingsContext';
import { convertSingleValue, japaneseTickFormatter, currencyToSymbol } from '../../utils/commonUtils';
import { fetchStockData, fetchTickData, getTickDateRange } from '../../api/api_calls';
import { useDashboardContext } from '../../../context/DashboardContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { MousePointerSquareDashed } from 'lucide-react';


const SummaryMarketGraph = ({ column, timeRange, setTimeRange, stockCurrency, reportedCurrency, setLoaded }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state: dashboardState, dispatch: dashboardDispatch } = useDashboardContext();
    const { selectableAccounts } = useContext(UserSettingsContext);
    const [stockPrice, setStockPrice] = useState(null);
    const [minClose, setMinClose] = useState(0);
    const [displayData, setDisplayData] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const [closeHour, setCloseHour] = useState(null);
    const [lastDateClose, setLastDateClose] = useState(null);
    const marketColumns = selectableAccounts.filter((account) => (account?.category_1?.category === 'market' || account?.col === 'dividend_yield'))
    const formatDate = (date) => {
        return format(new Date(date), 'yyyy-MM');  // 日付を "YYYY-MM" にフォーマット
    };

    const targetColumn = marketColumns.filter((col) => col.col === column)
    const unit = targetColumn[0].unit === 'value' ?  currencyToSymbol(stockCurrency) : targetColumn[0].unit;

    const dataKey = column === 'stock_price' ? 'close' : column;

    const timeRanges = [
        ...( ['stock_price', 'volume'].includes(column) ? [{ label: '1D', value: '1D' }] : [] ),
        { label: '5D', value: '5D' },
        { label: '1M', value: '1M' },
        { label: '6M', value: '6M' },
        { label: '1Y', value: '1Y' },
        { label: '3Y', value: '3Y' },
        { label: '5Y', value: '5Y' },
        { label: '10Y', value: '10Y' },
        { label: '20Y', value: '20Y' },
        { label: 'MAX', value: 'MAX' }
    ]
    const getPrevPrice = (data, firstDate) => {
        if (!data || data.length === 0) return null;
    
        // firstDateより小さい最大の日付を持つデータを取得
        const filteredData = data.filter(item => item.date < firstDate);
        if (filteredData.length === 0) return null;
    
        // 最大の日付を持つデータを取得
        const prevPriceData = filteredData[filteredData.length - 1]
        const prevPrice = prevPriceData[column === 'stock_price' ? 'close' : column]
        console.log(prevPriceData)
        return prevPrice;
    };
    

    const getTickData = async (data) => {
        setShowLoading(true)
        const secCode = data[0].sec_code;
        try {
            const { dateFrom, dateTo } = getTickDateRange();
            const response = await fetchTickData(secCode, dateFrom, dateTo);
            const fetchedData = response.data // データを逆順にする
            if (fetchedData.length === 0) return [];
    
            // 最初の要素の日付（YYYY-MM-DD形式に変換）
            const firstDate = fetchedData[0].date.split(' ')[0];
            // 同じ日付のデータだけをフィルタリング
            const filteredData = fetchedData.reverse().filter(item => item.date.startsWith(firstDate));

            const prevPrice = getPrevPrice(data, firstDate)
            setLastDateClose(prevPrice)

            setShowLoading(false)
            return filteredData;
        } catch (error) {
            console.error('Error in getTickData:', error);
            setShowLoading(false)
            return [];
        }
    };
    

    const makeDisplayData = async (data) => {
        if (!data || data.length === 0) return 0;

        let displayData;
        displayData = data.filter((dt) => dt[dataKey]);
        if (displayData.length < 2) return data;

        const latestDate = new Date(displayData[displayData.length - 1].date);
        let startDate = new Date(latestDate);
        switch (timeRange) {
            case '1D':
                displayData = await getTickData(data)
                break;
            case '5D':
                displayData = displayData.slice(displayData.length - 5, displayData.length - 1)
                break;
            case '1M':
                startDate.setMonth(latestDate.getMonth() - 1);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                break;
            case '6M':
                startDate.setMonth(latestDate.getMonth() - 6);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                break;
            case '1Y':
                startDate.setFullYear(latestDate.getFullYear() - 1);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                break;
            case '3Y':
                startDate.setFullYear(latestDate.getFullYear() - 3);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                break;
            case '5Y':
                startDate.setFullYear(latestDate.getFullYear() - 5);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                break;
            case '10Y':
                    startDate.setFullYear(latestDate.getFullYear() - 10);
                    displayData = displayData.filter(item => new Date(item.date) >= startDate);
                    break;
            case '20Y':
                startDate.setFullYear(latestDate.getFullYear() - 20);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                break;
            case 'MAX':
                break;
            default: 
                break;                

        }
        setDisplayData(displayData)
    } 

    const calculateMinClose = (data) => {
        if (!data || data.length === 0) return 0;
        const minClose = Math.min(...data.map(item => item[dataKey]));
        if (timeRange === '5D') {
            return minClose * 0.99;
        } else if (timeRange == '1D') {
            return Math.min(minClose * 0.99, lastDateClose * 0.99);
        } else {
            return minClose * 0.9;
        }
    };
    
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const date = label; // ラベル（日付）
            const value = payload[0].value; // close
            const targetColumn = marketColumns.filter((col) => col.col === column);
            const name = targetColumn[0].col_name;
            return (
                <div className="custom-tooltip text-white px-2 py-1 border border-gray-300 bg-gray-600 rounded shadow">
                    {timeRange == '1D' ? <div className="text-sm text-white">{format(new Date(date), 'dd日 HH:mm')}</div>
                    :
                    <div className="text-sm text-white">{format(new Date(date), 'yyyy-MM-dd')}</div>
                    }
                    <div className="text-sm text-white">{`${name}: ${convertSingleValue(value)}${unit}`}</div>
                </div>
            );
        }
        return null;
    };

    useEffect(() => {
        makeDisplayData(stockPrice);
    }, [stockPrice, timeRange]); 


        
    useEffect(() => {
        if (displayData) {
            const fetchMinClose = () => {
                const minClose = calculateMinClose(displayData);
                setMinClose(minClose);
            };
            fetchMinClose();
            
            if (timeRange === '1D' && displayData.length > 0) {
                const date = displayData[0].date; // YYYY-MM-DD HH:mm:ss
                if (stockCurrency === 'JPY') {
                    setCloseHour('16:00');
                } else {
                    setCloseHour('15:00');
                }
            }
        }
    }, [displayData]);

    useEffect(() => {
        const getStockData = async (secCode) => {
            // 昇順 
            let stockData;
            if (dashboardState.stockPriceData) {
                stockData = dashboardState.stockPriceData;
            } else {
                const fetchedData = await fetchStockData(secCode, null, navigate, location);
                stockData = fetchedData.data;
                dashboardDispatch({type: 'SET_STOCK_PRICE_DATA', payload: stockData})
            }
            setStockPrice(stockData);
        }
        getStockData(dashboardState.secCode);
        setLoaded((prev) => !prev)
    }, [])

    if (!stockPrice) {
        return <Loading />
    }

    if (['per', 'pbr', 'psr'].includes(column) && !['JPY', 'USD'].includes(reportedCurrency)) {
        return <div>日本・米国以外の企業の PER, PSR, PBR は現在準備中です</div>
    }

    return (
        <div className="summary-graph-container">
            <div className='mb-4'>
                <TimeRangeContainer timeRanges={timeRanges} handleTimeRangeSetting={setTimeRange} selectedRange={timeRange} />
            </div>
            {
            showLoading ? 
                <div className='h-[400px]'>
                    <Loading />
                </div>
            :
                <div className="dashboard-graphs summary-stock-price-graph">
                    <ResponsiveContainer width="100%" height={440}>
                        <AreaChart data={displayData}>
                            <defs>
                            <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#66cdaa" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#81ccb3" stopOpacity={0} />
                            </linearGradient>
                            </defs>
                            <XAxis 
                                dataKey="date" 
                                tick={{fontSize: 8}} 
                                tickFormatter={(value) => timeRange=='1D' ? format(new Date(value), 'HH:mm') : formatDate(value)}  // 日付を簡潔に表示
                                interval="preserveStartEnd"  // 両端を保持しつつ間隔を調整
                                minTickGap={20}  // ティック間の最小ギャップを設定
                                // domain={['auto', timeRange === '1D' && closeHour ? closeHour : 'auto']}
                                />
                            <YAxis 
                                tick={{fontSize: 8}} 
                                tickFormatter={(value) => japaneseTickFormatter(value, unit)}
                                domain={[
                                    minClose,
                                    (dataMax) => Math.max(dataMax, timeRange=='1D' ? lastDateClose * 1.02 : dataMax * 1.02) 
                                  ]}
                                orientation="right"
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="linear"
                                dataKey={dataKey}
                                stroke="rgb(45, 186, 77)" 
                                fillOpacity={1}
                                fill="url(#colorPrice)"
                            />
                            
                            {timeRange === '1D' && (
                                <ReferenceLine 
                                    y={lastDateClose} 
                                    stroke="gray" 
                                    strokeDasharray="3 3" 
                                    label={({ viewBox }) => (
                                    <g>
                                        <text 
                                        x={viewBox.x + 10} 
                                        y={viewBox.y - 5} 
                                        fill="gray" 
                                        fontSize="10" 
                                        textAnchor="start"
                                        >
                                        前日終値: {japaneseTickFormatter(lastDateClose, unit)}
                                        </text>
                                    </g>
                                    )}
                                    />
                                )}
                            <Brush dataKey="date" height={15} stroke="lightgrey" />
                        </AreaChart>
                    </ResponsiveContainer>
            
                </div>
            }
        </div>
    )
}

export default SummaryMarketGraph